import { Stack } from '@evaneos/b2b-ui';
import cx from 'classnames';
import ReactCountryFlag from 'react-country-flag';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import { FlightTakeoff } from '../../../design-system/Icons/FlightTakeoff';
import { LocationOn } from '../../../design-system/Icons/LocationOn';
import { MonetizationOn } from '../../../design-system/Icons/MonetizationOn';
import { People } from '../../../design-system/Icons/People';
import { Rating } from '../../../design-system/Rating';
import { DisplayProject } from '../../../resources/dossier-overview/types';

import s from './Project.css';

type Props = DisplayProject & {
    hasMultipleMarkets?: boolean;
    displayRating?: boolean;
    rating: number;
    adjustedBudget: null | number;
    isDuplicate: boolean;
    /* FIXME: only works on dossier overview */
    requesterIsRepeater?: boolean;
};

export const Project = ({
    arrivalDate,
    idealBudget,
    adjustedBudget,
    destinationLabel,
    displayRating,
    duration,
    expectedArrivalDate,
    hasMultipleMarkets,
    marketCode,
    paxNumber,
    requesterName,
    rating,
    isDuplicate,
}: Props): JSX.Element => {
    const { formatMessage } = useIntl();

    return (
        <div className={s.projectContainer}>
            <div
                className={cx('badge', s.projectRequester, {
                    [s.duplicate]: isDuplicate,
                })}
            >
                {hasMultipleMarkets && marketCode && (
                    <span>
                        <ReactCountryFlag countryCode={marketCode} svg />
                    </span>
                )}
                <div className={s.requester}>
                    <Stack direction="row">
                        <span>{requesterName}</span>
                        {displayRating && rating && !isDuplicate && <Rating value={rating} />}
                        {isDuplicate && ' - ' + formatMessage({ id: 'dossier.duplicate.mark' })}
                    </Stack>
                </div>
            </div>
            <div className={s.projectInformation}>
                {Boolean(destinationLabel) && (
                    <div>
                        <LocationOn
                            className={s.projectIcon}
                            color="var(--ink-light)"
                            width="20px"
                            height="20px"
                        />
                        <p className="annotation">{destinationLabel}</p>
                    </div>
                )}
                <div>
                    <FlightTakeoff
                        className={s.projectIcon}
                        color="var(--ink-light)"
                        width="20px"
                        height="20px"
                    />
                    {expectedArrivalDate && (
                        <p className="annotation">
                            <FormattedDate
                                value={expectedArrivalDate}
                                day={arrivalDate ? '2-digit' : undefined}
                                month="short"
                                year="numeric"
                            />
                        </p>
                    )}
                    {!expectedArrivalDate && <div className={s.emptyInformation}>——</div>}
                    <div className={s.separator}>•</div>
                    {Boolean(duration) && Boolean(arrivalDate) && (
                        <p className="annotation">
                            <FormattedMessage id="project.duration" values={{ days: duration }} />
                        </p>
                    )}
                    {Boolean(duration) && !arrivalDate && (
                        <p className="annotation">
                            <FormattedMessage
                                id="project.durationApproximate"
                                values={{ days: duration }}
                            />
                        </p>
                    )}
                    {!duration && <div className={s.emptyInformation}>——</div>}
                </div>
                <div>
                    <People
                        className={s.projectIcon}
                        color="var(--ink-light)"
                        width="20px"
                        height="20px"
                    />
                    {Boolean(paxNumber) && (
                        <p className="annotation">
                            <FormattedMessage id="project.paxNumber" values={{ pax: paxNumber }} />
                        </p>
                    )}
                    {!paxNumber && <div className={s.emptyInformation}>——</div>}
                </div>
                <div>
                    <MonetizationOn
                        className={s.projectIcon}
                        color="var(--ink-light)"
                        width="20px"
                        height="20px"
                    />
                    {idealBudget?.totalInEuro || adjustedBudget ? (
                        <p className="annotation">
                            <FormattedNumber
                                value={(adjustedBudget || idealBudget?.totalInEuro) as number}
                                style="currency"
                                minimumFractionDigits={0}
                                currency={'EUR'}
                            />
                        </p>
                    ) : (
                        <div className={s.emptyInformation}>——</div>
                    )}
                </div>
            </div>
        </div>
    );
};
